import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TTierSpecTalentVariant } from '../../../../../../../../shared/types/statisticsTypes';

type ThemeSelection = {
  themeKey: string;
  nodeIds: number[];
};

type TalentDifferencesState = {
  hoveredVariant: TTierSpecTalentVariant | null;
  hoveredReferenceVariant: TTierSpecTalentVariant | null;

  selectedThemes: ThemeSelection[];
};

const initialState: TalentDifferencesState = {
  hoveredVariant: null,
  hoveredReferenceVariant: null,
  selectedThemes: []
};

export const talentDifferencesSlice = createSlice({
  name: 'talentDifferences',
  initialState,
  reducers: {
    setHoveredComparison(
      state,
      action: PayloadAction<{
        variant: TTierSpecTalentVariant | null;
        referenceVariant?: TTierSpecTalentVariant | null;
      }>
    ) {
      const { variant, referenceVariant = null } = action.payload;
      state.hoveredVariant = variant;
      state.hoveredReferenceVariant = variant ? referenceVariant : null;
    },
    clearHoveredComparison(state) {
      state.hoveredVariant = null;
      state.hoveredReferenceVariant = null;
    },
    toggleTheme(state, action: PayloadAction<ThemeSelection>) {
      const exists = state.selectedThemes.some((t) => t.themeKey === action.payload.themeKey);
      state.selectedThemes = exists
        ? state.selectedThemes.filter((t) => t.themeKey !== action.payload.themeKey)
        : [...state.selectedThemes, action.payload];
    },
    // Add a batch of selections at once (skipping any already selected). Used to select a whole group of
    // categories in one click so its rows light up together rather than toggling one at a time.
    selectThemes(state, action: PayloadAction<ThemeSelection[]>) {
      const existing = new Set(state.selectedThemes.map((t) => t.themeKey));
      const additions = action.payload.filter((selection) => !existing.has(selection.themeKey));
      state.selectedThemes = [...state.selectedThemes, ...additions];
    },
    // Remove a batch of selections by theme key. Used to clear a whole group in one click.
    deselectThemes(state, action: PayloadAction<string[]>) {
      const removing = new Set(action.payload);
      state.selectedThemes = state.selectedThemes.filter((t) => !removing.has(t.themeKey));
    },
    clearAllThemeSelections(state) {
      state.selectedThemes = [];
    }
  }
});

export const {
  setHoveredComparison,
  clearHoveredComparison,
  toggleTheme,
  selectThemes,
  deselectThemes,
  clearAllThemeSelections
} = talentDifferencesSlice.actions;

type RootSlice = { talentDifferences: TalentDifferencesState };

export const selectHoveredVariant = (state: RootSlice) => state.talentDifferences.hoveredVariant;
export const selectHoveredReferenceVariant = (state: RootSlice) => state.talentDifferences.hoveredReferenceVariant;

export const selectSelectedThemes = (state: RootSlice) => state.talentDifferences.selectedThemes;
export const selectSelectedThemeKeys = (state: RootSlice) =>
  state.talentDifferences.selectedThemes.map((t) => t.themeKey);
export const selectHighlightNodeIds = (state: RootSlice) =>
  state.talentDifferences.selectedThemes.flatMap((t) => t.nodeIds);
